import * as React from 'react';

import { cn } from '@/lib/utils';
import type { ServiceCategory } from '@/types/order.types';

// Soft-tint pill for an ordered service in the "Services Ordered" column.
// Category drives the hue (property = vermillion, asset = deep brown); an
// optional quantity renders as a trailing count badge.
const CATEGORY_CLASSES: Record<ServiceCategory, string> = {
  property:
    'bg-service-property-soft text-service-property-soft-foreground border-service-property-soft-border',
  asset:
    'bg-service-asset-soft text-service-asset-soft-foreground border-service-asset-soft-border',
};

interface ServicePillProps extends React.HTMLAttributes<HTMLSpanElement> {
  category: ServiceCategory;
  /** Selected quantity shown as a trailing count badge. Omit or null to hide. */
  quantity?: number | null;
  /** Renders the pill as a removed/original service: muted grey, struck
      through, no quantity badge. Used for the "originally ordered" line. */
  removed?: boolean;
  children: React.ReactNode;
}

/**
 * Compact pill identifying a PlanOmatic service (photography, floorplan, etc.) on order and asset rows.
 *
 * @category Data
 */
export const ServicePill = React.forwardRef<HTMLSpanElement, ServicePillProps>(
  ({ category, quantity, removed = false, className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        // min-h (not a fixed h) lets the pill grow when a long name wraps;
        // items-center keeps the quantity badge vertically centered against the
        // full (possibly multi-line) label so it never looks misaligned.
        'inline-flex min-h-[23px] items-center gap-[5px] rounded-full border px-[9px] py-[3px] text-xs font-medium leading-tight',
        removed
          ? 'border-border bg-muted text-muted-foreground line-through'
          : CATEGORY_CLASSES[category],
        className,
      )}
      {...props}
    >
      {children}
      {!removed && quantity != null && (
        // Quantity reads as a quiet trailing figure rather than a filled chip:
        // a faint hairline in the pill's own accent (currentColor) separates it
        // from the label, and the number keeps full accent strength to stay
        // legible.
        <>
          <span
            aria-hidden="true"
            className="h-[11px] w-px bg-current opacity-25"
          />
          <span className="text-[11px] font-semibold leading-none">
            {quantity}
          </span>
        </>
      )}
    </span>
  ),
);
ServicePill.displayName = 'ServicePill';
